import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../../services/api";
import html2pdf from "html2pdf.js";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import "./studentDetails.css";

export default function StudentDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const school = JSON.parse(localStorage.getItem("schoolAdmin"));

  const [student, setStudent] = useState(null);
  const [studentLoading, setStudentLoading] = useState(true);
  const [studentError, setStudentError] = useState("");
  const [studentFees, setStudentFees] = useState([]);
  const [photoFailed, setPhotoFailed] = useState(false);
  const [feesLoading, setFeesLoading] = useState(false);
  const [attendance, setAttendance] = useState([]);
  const [classTests, setClassTests] = useState([]);
  const [testLoading, setTestLoading] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const formatValue = (value) => value || "-";

  const getPhotoUrl = (photo) => {
    if (!photo) return "";
    const url = photo.startsWith("http") ? photo : `https://navith.cloud${photo}`;
    return encodeURI(url);
  };

  const feeSummary = studentFees.reduce(
    (summary, fee) => {
      const total = Number(fee.amount || 0);
      const paid = Number(fee.paid_amount || 0);

      return {
        total: summary.total + total,
        paid: summary.paid + paid,
        pending: summary.pending + (total - paid),
      };
    },
    { total: 0, paid: 0, pending: 0 },
  );

  const attendanceRate =
    attendance.length > 0
      ? Math.min(100, Math.round((attendance.length / 31) * 100))
      : 0;

  const loadFees = async () => {
    try {
      setFeesLoading(true);

      const res = await API.get("/school-admin/addon-fees/", {
        params: {
          student_id: id,
        },
        headers: { "X-SCHOOL-ID": school.school.id },
      });

      setStudentFees(res.data.addon_fees || []);
    } catch (err) {
      console.error("Fees load error", err);
      setStudentFees([]);
    } finally {
      setFeesLoading(false);
    }
  };
  const loadClassTests = async () => {
    try {
      setTestLoading(true);

      const res = await API.post("/class-test/student/", {
        school_code: school.school.subdomain,
        student_id: id,
      });

      setClassTests(res.data.results || []);
    } catch (err) {
      console.error("Failed to load class tests", err);

      setClassTests([]);
    } finally {
      setTestLoading(false);
    }
  };
  const loadStudent = async () => {
    try {
      setStudentLoading(true);
      setStudentError("");
      const res = await API.get(`/school-admin/students/${id}/`, {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setStudent(res.data);
      setPhotoFailed(false);
    } catch (err) {
      console.error("Failed to load student", err);
      setStudentError("Unable to load this student profile.");
      setStudent(null);
    } finally {
      setStudentLoading(false);
    }
  };
  useEffect(() => {
    loadFees();
  }, [month, year, id]);
  const loadAttendance = async () => {
    try {
      setAttendanceLoading(true);

      const res = await API.get("/student/attendance/", {
        params: {
          school_code: school.school.subdomain,
          student_id: id,
          month: month,
          year: year,
        },
      });

      setAttendance(res.data.records || []);
    } catch (err) {
      console.error("Failed to load attendance", err);
      setAttendance([]);
      alert("Failed to load attendance");
    } finally {
      setAttendanceLoading(false);
    }
  };

  useEffect(() => {
    loadStudent();

    loadClassTests();
  }, [id]);
  const deleteStudent = async () => {
    if (!window.confirm("Delete this student?")) return;

    try {
      await API.delete(`/school-admin/students/delete/${id}/`, {
        headers: { "X-SCHOOL-ID": school.school.id },
      });

      alert("Student deleted");
      navigate("/school-admin/students");
    } catch (err) {
      console.error("Delete failed", err);
      alert("Failed to delete student");
    }
  };

  const exportPdfElement = async (element, filename) => {
    const stage = document.createElement("div");
    const clone = element.cloneNode(true);

    const waitForImages = async (root) => {
      const images = Array.from(root.querySelectorAll("img"));

      await Promise.all(
        images.map((image) => {
          if (image.complete && image.naturalWidth > 0) {
            return Promise.resolve();
          }

          return new Promise((resolve) => {
            image.onload = resolve;
            image.onerror = resolve;
          });
        }),
      );
    };

    stage.style.position = "fixed";
    stage.style.left = "0";
    stage.style.top = "0";
    stage.style.width = "794px";
    stage.style.minHeight = "1123px";
    stage.style.padding = "0";
    stage.style.background = "#ffffff";
    stage.style.zIndex = "999999";
    stage.style.pointerEvents = "none";

    clone.style.display = "block";
    clone.style.width = "794px";
    clone.style.maxWidth = "794px";
    clone.style.margin = "0";
    clone.style.background = "#ffffff";

    stage.appendChild(clone);
    document.body.appendChild(stage);

    try {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await waitForImages(clone);

      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        imageTimeout: 5000,
        backgroundColor: "#ffffff",
        windowWidth: 794,
        windowHeight: Math.max(1123, clone.scrollHeight),
      });

      const imgData = canvas.toDataURL("image/jpeg", 1);
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pageWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position -= pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, pageWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(filename);
    } finally {
      stage.remove();
    }
  };

  const exportPDF = () => {
    const photoUrl = student.photo ? getPhotoUrl(student.photo) : "";
    const schoolName = school?.school?.name || "School Name";
    const classLine = `${formatValue(student.class_name)} / ${formatValue(student.section_name)} / Roll ${formatValue(student.roll_number)}`;
    const printWindow = window.open("", "_blank", "width=900,height=1100");

    if (!printWindow) {
      alert("Please allow popups to download the profile.");
      return;
    }

    const escapeHtml = (value) =>
      String(formatValue(value))
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const safeSchoolName = escapeHtml(schoolName);
    const safeStudentName = escapeHtml(student.name);
    const safeClassLine = escapeHtml(classLine);
    const safeInitial = escapeHtml(student.name?.charAt(0) || "S");

    const field = (label, value) => `
      <div class="field">
        <b>${escapeHtml(label)}</b>
        <span>${escapeHtml(value)}</span>
      </div>
    `;

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${formatValue(student.name)} Profile</title>
          <style>
            * { box-sizing: border-box; }
            body {
              margin: 0;
              padding: 28px;
              color: #0f172a;
              background: #ffffff;
              font-family: Arial, sans-serif;
            }
            .sheet {
              max-width: 800px;
              margin: 0 auto;
            }
            .header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 24px;
              padding-bottom: 18px;
              border-bottom: 2px solid #e2e8f0;
            }
            h1 {
              margin: 0 0 6px;
              color: #111827;
              font-size: 24px;
            }
            .header p,
            .title p {
              margin: 0;
              color: #64748b;
              font-size: 13px;
              font-weight: 700;
            }
            .photo {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 112px;
              height: 112px;
              overflow: hidden;
              border: 2px solid #e2e8f0;
              border-radius: 12px;
              background: #f1f5f9;
              color: #6b21a8;
              font-size: 42px;
              font-weight: 900;
            }
            .photo img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
            .title {
              margin-top: 20px;
              padding: 16px;
              border: 1px solid #dbe4ee;
              border-radius: 10px;
              background: #f8fafc;
            }
            .title h2 {
              margin: 0 0 6px;
              font-size: 22px;
            }
            .section {
              margin-top: 18px;
            }
            .section h3 {
              margin: 0 0 10px;
              color: #1e40af;
              font-size: 15px;
            }
            .grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
            }
            .field {
              min-height: 48px;
              padding: 10px 12px;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
            }
            .field.wide {
              grid-column: span 2;
            }
            .field b {
              display: block;
              margin-bottom: 4px;
              color: #64748b;
              font-size: 10px;
              text-transform: uppercase;
            }
            .field span {
              display: block;
              overflow-wrap: anywhere;
              font-size: 13px;
              font-weight: 700;
            }
            @page { size: A4; margin: 14mm; }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="sheet">
            <div class="header">
              <div>
                <h1>${safeSchoolName}</h1>
                <p>Student Profile</p>
              </div>
              <div class="photo">
                ${
                  photoUrl
                    ? `<img src="${escapeHtml(photoUrl)}" alt="${safeStudentName}" />`
                    : `<span>${safeInitial}</span>`
                }
              </div>
            </div>

            <div class="title">
              <h2>${safeStudentName}</h2>
              <p>${safeClassLine}</p>
            </div>

            <div class="section">
              <h3>Basic Info</h3>
              <div class="grid">
                ${field("Student ID", student.student_id)}
                ${field("Name", student.name)}
                ${field("Gender", student.gender)}
                ${field("DOB", student.dob)}
                ${field("Class", student.class_name)}
                ${field("Section", student.section_name)}
                ${field("Roll", student.roll_number)}
                ${field("Religion", student.religion)}
                ${field("EMIS No", student.emis_number)}
                ${field("Blood Group", student.blood_group)}
                ${field("Aadhaar No", student.aadhaar_number)}
              </div>
            </div>

            <div class="section">
              <h3>Login Details</h3>
              <div class="grid">
                ${field("Student ID", student.student_id)}
                ${field("Student Password", student.student_password)}
                ${field("Parent ID", student.parent_id)}
                ${field("Parent Password", student.parent_password)}
              </div>
            </div>

            <div class="section">
              <h3>Parent Info</h3>
              <div class="grid">
                ${field("Parent Name", student.parent_name)}
                ${field("Father Name", student.father_name)}
                ${field("Mother Name", student.mother_name)}
                ${field("Guardian Name", student.guardian_name)}
                ${field("Phone", student.parent_phone)}
                ${field("Email", student.parent_email)}
                ${field("Qualification", student.parent_qualification)}
                ${field("Income", student.parent_income)}
                <div class="field wide"><b>Address</b><span>${escapeHtml(student.address)}</span></div>
                <div class="field wide"><b>Notes</b><span>${escapeHtml(student.notes)}</span></div>
              </div>
            </div>
          </div>

          <script>
            const images = Array.from(document.images);
            Promise.all(images.map((img) => {
              if (img.complete) return Promise.resolve();
              return new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
              });
            })).then(() => {
              window.focus();
              window.print();
            });
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const generateInvoice = () => {
    const element = document.getElementById("invoice-box");

    if (!element) {
      alert("Invoice content not found!");
      return;
    }

    exportPdfElement(element, `${student.name}_invoice.pdf`);
  };

  const generateSingleInvoice = (fee) => {
    const element = document.getElementById("single-invoice-box");

    if (!element) {
      alert("Invoice template not found!");
      return;
    }

    // Inject dynamic data
    document.getElementById("inv-name").innerText = student.name;
    document.getElementById("inv-class").innerText = student.class_name;
    document.getElementById("inv-section").innerText = student.section_name;

    document.getElementById("inv-fee-title").innerText = fee.title;
    document.getElementById("inv-total").innerText = fee.amount;
    document.getElementById("inv-paid").innerText = fee.paid_amount;
    document.getElementById("inv-remaining").innerText =
      Number(fee.amount) - Number(fee.paid_amount);

    exportPdfElement(element, `${student.name}_${fee.title}_invoice.pdf`);
  };

  const exportAttendancePDF = () => {
    const element = document.getElementById("attendance-pdf-box");

    if (!element) {
      alert("Attendance section not found!");
      return;
    }

    if (!attendance.length) {
      alert("No attendance data to download");
      return;
    }

    html2pdf()
      .set({
        margin: 0.5,
        filename: `${student.name}_attendance_${month}_${year}.pdf`,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          scale: 2,
          useCORS: true, // ✅ fixes image issues
        },
        jsPDF: {
          unit: "cm",
          format: "a4",
          orientation: "portrait",
        },
      })
      .from(element)
      .save();
  };

  if (studentLoading) {
    return (

        <div className="profile-container">
          <div className="profile-loading-card">
            <span className="profile-loader"></span>
            <strong>Loading student profile</strong>
            <p>Preparing personal details, fees, attendance, and results.</p>
          </div>
        </div>

    );
  }

  if (studentError || !student) {
    return (

        <div className="profile-container">
          <div className="profile-loading-card profile-error-card">
            <strong>{studentError || "Student profile was not found."}</strong>
            <p>Return to the student directory or try loading the profile again.</p>
            <div className="profile-empty-actions">
              <button onClick={() => navigate("/school-admin/students")}>
                Student List
              </button>
              <button onClick={loadStudent}>
                Retry
              </button>
            </div>
          </div>
        </div>

    );
  }

  return (

      <div className="profile-container">
        {/* ===== HEADER ===== */}
        <div className="profile-hero">
          <div>
            <button
              className="profile-back-btn"
              onClick={() => navigate("/school-admin/students")}
            >
              Back to Students
            </button>
            <h2>{student.name || "Student Profile"}</h2>
            <p>
              {formatValue(student.class_name)} / {formatValue(student.section_name)} / Roll {formatValue(student.roll_number)}
            </p>
          </div>

          <div className="header-actions">
            <button
              className="btn-secondary"
              onClick={exportPDF}
            >
              Download Profile
            </button>
            <button
              className="btn-edit"
              onClick={() => navigate(`/school-admin/students/edit/${id}`)}
            >
              Edit Profile
            </button>
          </div>
        </div>

        <div className="profile-stat-grid">
          <div className="profile-stat-card">
            <span>Student ID</span>
            <strong>{formatValue(student.student_id)}</strong>
          </div>
          <div className="profile-stat-card">
            <span>Fee Pending</span>
            <strong>₹{feeSummary.pending}</strong>
          </div>
          <div className="profile-stat-card">
            <span>Present Days</span>
            <strong>{attendance.length}</strong>
          </div>
          <div className="profile-stat-card">
            <span>Class Tests</span>
            <strong>{classTests.length}</strong>
          </div>
        </div>

        {/* ===== MAIN GRID ===== */}
        <div className="profile-grid" id="student-pdf-box">
          {/* ===== LEFT SIDE ===== */}
          <div className="profile-left">
            {/* PHOTO */}
            <div className="profile-photo-box">
              {student.photo ? (
                <img
                  src={getPhotoUrl(student.photo)}
                  alt={student.name || "Student"}
                  className="profile-img"
                  referrerPolicy="no-referrer"
                  onError={() => setPhotoFailed(true)}
                />
              ) : (
                <div className="profile-img profile-initial">
                  {student.name?.charAt(0) || "S"}
                </div>
              )}
              <div className="profile-photo-caption">
                <strong>{formatValue(student.name)}</strong>
                <span>{formatValue(student.student_id)}</span>
              </div>
            </div>

            {/* ================= BASIC INFO ================= */}
            <div className="profile-block">
              <h4>Basic Info</h4>

              <div className="info-grid">
                <div className="info-item">
                  <span>Name</span>
                  <b>{formatValue(student.name)}</b>
                </div>

                <div className="info-item">
                  <span>Gender</span>
                  <b>{formatValue(student.gender)}</b>
                </div>

                <div className="info-item">
                  <span>DOB</span>
                  <b>{formatValue(student.dob)}</b>
                </div>

                <div className="info-item">
                  <span>Class</span>
                  <b>{formatValue(student.class_name)}</b>
                </div>

                <div className="info-item">
                  <span>Section</span>
                  <b>{formatValue(student.section_name)}</b>
                </div>

                <div className="info-item">
                  <span>Roll</span>
                  <b>{formatValue(student.roll_number)}</b>
                </div>

                {/* NEW */}
                <div className="info-item">
                  <span>Religion</span>
                  <b>{student.religion || "-"}</b>
                </div>

                <div className="info-item">
                  <span>EMIS No</span>
                  <b>{student.emis_number || "-"}</b>
                </div>

                <div className="info-item">
                  <span>Blood Group</span>
                  <b>{student.blood_group || "-"}</b>
                </div>

                <div className="info-item">
                  <span>Aadhaar No</span>
                  <b>{student.aadhaar_number || "-"}</b>
                </div>
              </div>
            </div>

            {/* ================= LOGIN DETAILS ================= */}
            <div className="profile-block">
              <h4>Login Details</h4>

              <div className="info-grid">
                <div className="info-item">
                  <span>Student ID</span>
                  <b>{formatValue(student.student_id)}</b>
                </div>

                <div className="info-item">
                  <span>Student Password</span>
                  <b>{formatValue(student.student_password)}</b>
                </div>

                <div className="info-item">
                  <span>Parent ID</span>
                  <b>{formatValue(student.parent_id)}</b>
                </div>

                <div className="info-item">
                  <span>Parent Password</span>
                  <b>{formatValue(student.parent_password)}</b>
                </div>
              </div>
            </div>

            {/* ================= PARENT INFO ================= */}
            <div className="profile-block">
              <h4>Parent Info</h4>

              <div className="info-grid">
                <div className="info-item">
                  <span>Parent Name</span>
                  <b>{student.parent_name || "-"}</b>
                </div>

                <div className="info-item">
                  <span>Father Name</span>
                  <b>{student.father_name || "-"}</b>
                </div>

                <div className="info-item">
                  <span>Mother Name</span>
                  <b>{student.mother_name || "-"}</b>
                </div>

                <div className="info-item">
                  <span>Guardian Name</span>
                  <b>{student.guardian_name || "-"}</b>
                </div>

                <div className="info-item">
                  <span>Phone</span>
                  <b>{student.parent_phone || "-"}</b>
                </div>

                <div className="info-item">
                  <span>Email</span>
                  <b>{student.parent_email || "-"}</b>
                </div>

                <div className="info-item">
                  <span>Qualification</span>
                  <b>{student.parent_qualification || "-"}</b>
                </div>

                <div className="info-item">
                  <span>Income</span>
                  <b>{student.parent_income || "-"}</b>
                </div>

                <div className="info-item full">
                  <span>Address</span>
                  <b>{student.address || "-"}</b>
                </div>

                <div className="info-item full">
                  <span>Notes</span>
                  <b>{student.notes || "-"}</b>
                </div>
              </div>
            </div>
          </div>

          {/* ===== RIGHT SIDE ===== */}
          <div className="profile-right">
            {/* ===== FEES ===== */}
            <div className="card">
              <div className="premium-card-header">
                <div>
                  <h3>Fees</h3>
                  <p>Total ₹{feeSummary.total} / Paid ₹{feeSummary.paid}</p>
                </div>
                <button
                  className="btn-invoice-small"
                  onClick={generateInvoice}
                  disabled={studentFees.length === 0}
                >
                  Download Invoice
                </button>
              </div>

              {feesLoading ? (
                <div className="premium-empty-state">
                  <span className="profile-loader"></span>
                  <strong>Loading fee records</strong>
                </div>
              ) : studentFees.length === 0 ? (
                <div className="premium-empty-state">
                  <strong>No fee records</strong>
                  <p>Fee entries for this student will appear here after they are generated.</p>
                </div>
              ) : (
                <div className="premium-table-wrap">
                  <table className="fees-table">
                    <thead>
                      <tr>
                        <th>Fee Type</th>
                        <th>Total</th>
                        <th>Paid</th>
                        <th>Remaining</th>
                        <th>Status</th>
                      </tr>
                    </thead>

                    <tbody>
                      {studentFees.map((f) => {
                        const total = Number(f.amount || 0);
                        const paid = Number(f.paid_amount || 0);
                        const remaining = total - paid;

                        return (
                          <tr key={f.id}>
                            <td>{f.title}</td>
                            <td>₹{total}</td>

                            <td>
                              <div className="paid-box">
                                ₹{paid}
                                <div className="update-row">
                                  <input
                                    type="number"
                                    placeholder="+ / -"
                                    className="input-small"
                                    value={f.input_value || ""}
                                    onChange={(e) => {
                                      const value = e.target.value;

                                      setStudentFees((prev) =>
                                        prev.map((item) =>
                                          item.id === f.id
                                            ? { ...item, input_value: value }
                                            : item,
                                        ),
                                      );
                                    }}
                                  />

                                  <button
                                    className="btn-green"
                                    onClick={async () => {
                                      const value = Number(f.input_value);
                                      if (!value) return;

                                      await API.post(
                                        `/school-admin/addon-fees/${f.id}/update-payment/`,
                                        { amount: value },
                                        {
                                          headers: {
                                            "X-SCHOOL-ID": school.school.id,
                                          },
                                        },
                                      );

                                      loadFees();
                                    }}
                                  >
                                    Update
                                  </button>
                                </div>
                              </div>
                            </td>

                            <td>₹{remaining}</td>

                            <td>
                              <div className="fee-status-wrap">
                                {remaining === 0 ? (
                                  <span className="badge green">Paid</span>
                                ) : paid === 0 ? (
                                  <span className="badge red">Unpaid</span>
                                ) : (
                                  <span className="badge orange">Pending</span>
                                )}

                                <button
                                  className="btn-invoice-small"
                                  onClick={() => generateSingleInvoice(f)}
                                >
                                  Invoice
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <div id="student-profile-pdf-box" className="invoice-box student-profile-pdf">
          <div className="student-profile-pdf-header">
            <div>
              <h1>{school?.school?.name || "School Name"}</h1>
              <p>Student Profile</p>
            </div>

            <div className="student-profile-pdf-photo">
              {student.photo && !photoFailed ? (
                <img
                  src={getPhotoUrl(student.photo)}
                  alt={student.name || "Student"}
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span>{student.name?.charAt(0) || "S"}</span>
              )}
            </div>
          </div>

          <div className="student-profile-pdf-title">
            <h2>{formatValue(student.name)}</h2>
            <p>
              {formatValue(student.class_name)} / {formatValue(student.section_name)} / Roll {formatValue(student.roll_number)}
            </p>
          </div>

          <div className="student-profile-pdf-section">
            <h3>Basic Info</h3>
            <div className="student-profile-pdf-grid">
              <p><b>Student ID</b><span>{formatValue(student.student_id)}</span></p>
              <p><b>Name</b><span>{formatValue(student.name)}</span></p>
              <p><b>Gender</b><span>{formatValue(student.gender)}</span></p>
              <p><b>DOB</b><span>{formatValue(student.dob)}</span></p>
              <p><b>Class</b><span>{formatValue(student.class_name)}</span></p>
              <p><b>Section</b><span>{formatValue(student.section_name)}</span></p>
              <p><b>Roll</b><span>{formatValue(student.roll_number)}</span></p>
              <p><b>Religion</b><span>{formatValue(student.religion)}</span></p>
              <p><b>EMIS No</b><span>{formatValue(student.emis_number)}</span></p>
              <p><b>Blood Group</b><span>{formatValue(student.blood_group)}</span></p>
              <p><b>Aadhaar No</b><span>{formatValue(student.aadhaar_number)}</span></p>
            </div>
          </div>

          <div className="student-profile-pdf-section">
            <h3>Login Details</h3>
            <div className="student-profile-pdf-grid">
              <p><b>Student ID</b><span>{formatValue(student.student_id)}</span></p>
              <p><b>Student Password</b><span>{formatValue(student.student_password)}</span></p>
              <p><b>Parent ID</b><span>{formatValue(student.parent_id)}</span></p>
              <p><b>Parent Password</b><span>{formatValue(student.parent_password)}</span></p>
            </div>
          </div>

          <div className="student-profile-pdf-section">
            <h3>Parent Info</h3>
            <div className="student-profile-pdf-grid">
              <p><b>Parent Name</b><span>{formatValue(student.parent_name)}</span></p>
              <p><b>Father Name</b><span>{formatValue(student.father_name)}</span></p>
              <p><b>Mother Name</b><span>{formatValue(student.mother_name)}</span></p>
              <p><b>Guardian Name</b><span>{formatValue(student.guardian_name)}</span></p>
              <p><b>Phone</b><span>{formatValue(student.parent_phone)}</span></p>
              <p><b>Email</b><span>{formatValue(student.parent_email)}</span></p>
              <p><b>Qualification</b><span>{formatValue(student.parent_qualification)}</span></p>
              <p><b>Income</b><span>{formatValue(student.parent_income)}</span></p>
              <p className="student-profile-pdf-wide"><b>Address</b><span>{formatValue(student.address)}</span></p>
              <p className="student-profile-pdf-wide"><b>Notes</b><span>{formatValue(student.notes)}</span></p>
            </div>
          </div>
        </div>

        <div id="single-invoice-box" className="invoice-box">
          <div className="invoice-header">
            <h1>{school?.school?.name || "School Name"}</h1>
            <p>Fee Invoice</p>
          </div>

          <div className="invoice-section">
            <p>
              <b>Name:</b> <span id="inv-name"></span>
            </p>
            <p>
              <b>Class:</b> <span id="inv-class"></span>
            </p>
            <p>
              <b>Section:</b> <span id="inv-section"></span>
            </p>
          </div>

          <table className="invoice-table">
            <thead>
              <tr>
                <th>Fee Type</th>
                <th>Total</th>
                <th>Paid</th>
                <th>Remaining</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td id="inv-fee-title"></td>
                <td>
                  ₹<span id="inv-total"></span>
                </td>
                <td>
                  ₹<span id="inv-paid"></span>
                </td>
                <td>
                  ₹<span id="inv-remaining"></span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {/* ===== HIDDEN INVOICE FOR PDF ===== */}
        <div id="invoice-box" className="invoice-box">
          {/* ===== HEADER ===== */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <h1>{school?.school?.name || "School Name"}</h1>
                <p>{school?.school?.subdomain}.navith.cloud</p>
              </div>

              <div style={{ textAlign: "right" }}>
                <p>
                  <b>Invoice No:</b> INV-{Date.now()}
                </p>
                <p>
                  <b>Date:</b> {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          <hr />

          {/* ===== STUDENT DETAILS ===== */}
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <p>
                <b>Name:</b> {student.name}
              </p>
              <p>
                <b>Class:</b> {student.class_name}
              </p>
              <p>
                <b>Section:</b> {student.section_name}
              </p>
              <p>
                <b>Roll No:</b> {student.roll_number}
              </p>
            </div>

            <div>
              <p>
                <b>Student ID:</b> {student.student_id}
              </p>
              <p>
                <b>Parent:</b> {student.parent_name}
              </p>
              <p>
                <b>Phone:</b> {student.parent_phone}
              </p>
            </div>
          </div>

          <br />

          {/* ===== FEES TABLE ===== */}
          <table className="invoice-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Fee Type</th>
                <th>Total</th>
                <th>Paid</th>
                <th>Remaining</th>
              </tr>
            </thead>

            <tbody>
              {studentFees.map((f, index) => {
                const total = Number(f.amount || 0);
                const paid = Number(f.paid_amount || 0);
                const remaining = total - paid;

                return (
                  <tr key={f.id}>
                    <td>{index + 1}</td>
                    <td>{f.title}</td>
                    <td>₹{total}</td>
                    <td>₹{paid}</td>
                    <td>₹{remaining}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <br />

          {/* ===== TOTAL SUMMARY ===== */}
          <div style={{ textAlign: "right" }}>
            {(() => {
              const total = studentFees.reduce(
                (a, f) => a + Number(f.amount || 0),
                0,
              );
              const paid = studentFees.reduce(
                (a, f) => a + Number(f.paid_amount || 0),
                0,
              );
              const remaining = total - paid;

              return (
                <>
                  <p>
                    Total Amount: <b>₹{total}</b>
                  </p>
                  <p>
                    Paid Amount: <b>₹{paid}</b>
                  </p>
                  <p>
                    Remaining: <b>₹{remaining}</b>
                  </p>
                </>
              );
            })()}
          </div>

          <br />

          {/* ===== NOTE ===== */}
          <div>
            <p style={{ fontSize: "12px" }}>
              This is a system-generated invoice. No signature required.
            </p>
          </div>
        </div>
        {/* ===== ATTENDANCE SECTION ===== */}
        <div className="card attendance-card" id="attendance-pdf-box">
          <div className="premium-card-header">
            <div>
              <h3>Attendance</h3>
              <p>{attendance.length} present days / {attendanceRate}% monthly view</p>
            </div>
          </div>

          <div className="attendance-controls">
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {monthOptions.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>

            <input
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            />

            <button onClick={loadAttendance}>
              {attendanceLoading ? "Loading..." : "View"}
            </button>

            <button onClick={exportAttendancePDF}>Download</button>
          </div>

          <div className="attendance-summary">
            <div>
              <span>Total Present</span>
              <strong>{attendance.length}</strong>
            </div>
            <div>
              <span>Selected Month</span>
              <strong>{monthOptions.find((m) => m.value === month)?.label}</strong>
            </div>
          </div>

          {attendance.length > 0 ? (
            <div className="attendance-list">
              {attendance.map((d, i) => (
                <span key={i} className="day-box">
                  {d}
                </span>
              ))}
            </div>
          ) : (
            <div className="premium-empty-state">
              <strong>No attendance records</strong>
              <p>Select a month and press View to load attendance.</p>
            </div>
          )}
        </div>

        {/* ===== CLASS TESTS ===== */}

        <div className="card attendance-card">
          <div className="premium-card-header">
            <div>
              <h3>Class Test Results</h3>
              <p>{classTests.length} recorded result{classTests.length === 1 ? "" : "s"}</p>
            </div>
          </div>

          {testLoading ? (
            <div className="premium-empty-state">
              <span className="profile-loader"></span>
              <strong>Loading class test results</strong>
            </div>
          ) : classTests.length === 0 ? (
            <div className="premium-empty-state">
              <strong>No class test results</strong>
              <p>Results will be visible here after class tests are published.</p>
            </div>
          ) : (
            <div className="premium-table-wrap">
              <table className="fees-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Subject</th>
                    <th>Test</th>
                    <th>Marks</th>
                    <th>Result</th>
                  </tr>
                </thead>

                <tbody>
                  {classTests.map((t, i) => (
                    <tr key={i}>
                      <td>{t.date}</td>

                      <td>{t.subject}</td>

                      <td>{t.test_name}</td>

                      <td>{t.marks}</td>

                      <td>
                        {t.result === "Pass" ? (
                          <span className="badge green">Pass</span>
                        ) : (
                          <span className="badge red">Fail</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

  );
}
